import { Stack } from '@fluentui/react';
import {
  Button,
  buttonStylesPrimary,
  buttonStylesSecondary,
  comboboxLoader,
  Loader,
  LoaderType,
  useClassNames,
} from '@h2oai/ui-kit';

import { formatDefaultDateAndTimeWithSeconds } from '../../../utils/utils';
import { AuditTrailListClassNames, AuditTrailListStyles, auditTrailListStylesDefault } from '../auditTrail.styles';
import { defaultColumns } from '../constants';
import { AuditTrailListProps } from '../types';
import { AuditTrailListRow } from './AuditTrailListRow';

export const AuditTrailList = (props: AuditTrailListProps) => {
  const { events, columns, loading, hasMorePages, onLoadMore, isLoadingMore, searchedUntilTime } = props;
  const noData = !loading && events.length === 0;
  const classNames = useClassNames<Partial<AuditTrailListStyles>, AuditTrailListClassNames>(
    'AuditTrail',
    auditTrailListStylesDefault
  );
  const disableLoadMore = loading || !hasMorePages;

  if (loading && events.length === 0) {
    return (
      <Stack className={classNames.root}>
        <Loader type={LoaderType.progressIndicator} styles={comboboxLoader} hidden={!loading} />
      </Stack>
    );
  }

  return (
    <Stack className={classNames.root}>
      <Loader type={LoaderType.progressIndicator} styles={comboboxLoader} hidden={!loading} />
      {noData ? (
        <Stack>No data available for these parameters</Stack>
      ) : (
        <table>
          <colgroup>
            <col width="20px" />
            <col width="20px" />
            <col width="250px" />
            <col width="400px" />
            {Array.isArray(columns) &&
              columns.slice(0, -defaultColumns.length).map((column) => <col width="300" key={column} />)}
            <col width="auto" />
          </colgroup>

          <thead>
            <AuditTrailListRow header columns={columns} />
          </thead>

          <tbody>
            {events.map((event) => (
              <AuditTrailListRow key={event.name} event={event} columns={columns} />
            ))}
          </tbody>
        </table>
      )}

      <Stack styles={{ root: { paddingTop: 8 } }}>
        <Loader type={LoaderType.progressIndicator} styles={comboboxLoader} hidden={!isLoadingMore} />
        <Stack styles={{ root: { paddingTop: 8 } }} tokens={{ childrenGap: 16 }} horizontal verticalAlign="center">
          <Button
            tooltip={hasMorePages ? '' : 'No more data to load'}
            text="Load More"
            styles={disableLoadMore ? buttonStylesSecondary : buttonStylesPrimary}
            onClick={disableLoadMore ? undefined : onLoadMore}
            disabled={disableLoadMore}
          />
          {searchedUntilTime && (
            <div>
              Scanned up to: <b>{formatDefaultDateAndTimeWithSeconds(searchedUntilTime)}</b>
            </div>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
